import React, { useState, useEffect } from 'react';
import { TabletLandscapeAndLarger, SmallerThanTabletLandscape } from '@montana/shared/responsive';
import { SvgIcon } from '@montana/shared/svg-icon';
import { IImage } from '@montana/models/generated/cms.interface';
import { CarouselProductImage } from '../carousel-product-image';
import { useProduct } from '@montana/containers/product';

export interface IImageCarousel {
    mobileLayout?: boolean;
}

export const ImageCarousel: React.FC<IImageCarousel> = React.memo(({
    mobileLayout = false,
}: IImageCarousel): JSX.Element => {

    const { professionalImages } = useProduct();
    const images = professionalImages ?? [];

    const [selectedImage, setSelectedImage] = useState<IImage>(images[0])

    const [prevArrowActive, setPrevArrowActive] = useState(false);
    const [nextArrowActive, setNextArrowActive] = useState(false);
    const [scroller, setScroller] = useState<HTMLElement | null>(null)
    const [arrowScrollSize, setArrowScrollSize] = useState(0)

    useEffect(() => {
        const scroller = document.getElementById('scroller')
        if (scroller) {
            setScroller(scroller);
            if (images.length > 4) {
                setNextArrowActive(true);
            }
        }
        updateArrowScrollSize();
    }, []);

    window.addEventListener("resize", () => {
		updateArrowScrollSize();
    });

    let pos = { left: 0, top: 0, x: 0, y: 0 };

    function updateArrows(scrollAmount: number) {
        if (scroller) {
            const maxScroll = mobileLayout
            ? scroller.scrollWidth - scroller.clientWidth
            : scroller.scrollHeight - scroller.clientHeight;

            if (scrollAmount <= 0) {
                setPrevArrowActive(false);
            }
            if (scrollAmount > 0) {
                setPrevArrowActive(true);
            }
            if (scrollAmount < maxScroll) {
                setNextArrowActive(true);
            }
            if (scrollAmount >= maxScroll) {
                setNextArrowActive(false);
            }
        }
    }

    function mouseDownHandler (e: TouchEvent | MouseEvent ) {
        if (scroller) {
            scroller.style.cursor = 'grabbing';
            scroller.style.userSelect = 'none';
            if (e instanceof TouchEvent) {
                pos = {
                    left: scroller.scrollLeft,
                    top: scroller.scrollTop,
                    x: e.changedTouches[0].clientX,
                    y: e.changedTouches[0].clientY,
                };
                document.addEventListener('touchmove', mouseMoveHandler);
                document.addEventListener('touchend', mouseUpHandler);
            }
            if (e instanceof MouseEvent) {
                pos = {
                    left: scroller.scrollLeft,
                    top: scroller.scrollTop,
                    x: e.clientX,
                    y: e.clientY,
                };
                document.addEventListener('mousemove', mouseMoveHandler);
                document.addEventListener('mouseup', mouseUpHandler);
            }
        }
    }

    function mouseMoveHandler (e: MouseEvent | TouchEvent) {
        if (scroller) {
            if (e instanceof TouchEvent) {
                if (mobileLayout) {
                    const dx = e.changedTouches[0].clientX - pos.x;
                    scroller.scrollLeft = pos.left - dx;
                    updateArrows(scroller.scrollLeft);
                }
                if (!mobileLayout) {
                    const dy = e.changedTouches[0].clientY - pos.y;
                    scroller.scrollTop = pos.top - dy;
                    updateArrows(scroller.scrollTop);
                }
            }
            if (e instanceof MouseEvent) {
                if (mobileLayout) {
                    const dx = e.clientX - pos.x;
                    scroller.scrollLeft = pos.left - dx;
                    updateArrows(scroller.scrollLeft);
                }
                if (!mobileLayout) {
                    const dy = e.clientY - pos.y;
                    scroller.scrollTop = pos.top - dy;
                    updateArrows(scroller.scrollTop);
                }
            }
        }
    }

    function mouseUpHandler () {
        if (scroller) {
            scroller.style.cursor = 'grab';
            scroller.style.removeProperty('user-select');
            document.removeEventListener('mousemove', mouseMoveHandler);
            document.removeEventListener('touchmove', mouseMoveHandler);
            document.removeEventListener('mouseup', mouseUpHandler);
            document.removeEventListener('touchend', mouseUpHandler);
        }
    }

    scroller?.addEventListener('mousedown', mouseDownHandler);
    scroller?.addEventListener('touchstart', mouseDownHandler);


    function onPrevArrowClick() {
        if (scroller) {
            if (mobileLayout) {
                pos.left = scroller.scrollLeft - arrowScrollSize;
                scroller.scroll({left: pos.left, behavior: 'smooth'})
                updateArrows(pos.left);
            }
            if (!mobileLayout) {
                pos.top = scroller.scrollTop - arrowScrollSize;
                scroller.scroll({top: pos.top, behavior: 'smooth'})
                updateArrows(pos.top);
            }
        }
    }

    function onNextArrowClick() {
        if (scroller) {
            if (mobileLayout) {
                pos.left = scroller.scrollLeft + arrowScrollSize;
                scroller.scroll({left: pos.left, behavior: 'smooth'})
                updateArrows(pos.left);
            }
            if (!mobileLayout) {
                pos.top = scroller.scrollTop + arrowScrollSize;
                scroller.scroll({top: pos.top, behavior: 'smooth'})
                updateArrows(pos.top);
            }
        }
    }

    function updateArrowScrollSize() {
        let scrollSize = (window.innerHeight / 10) + 8;
        if (scrollSize > 104) {
            scrollSize = 104;
        }
        setArrowScrollSize(scrollSize);
    }

    return (
        <>
            <TabletLandscapeAndLarger>
                <div className='image-carousel'>
                    <div className='image-carousel__desktop'>
                        <button
                            disabled={!prevArrowActive}
                            onClick={onPrevArrowClick}
                            type="button"
                            aria-label="Previous"
                        >
                            <SvgIcon
                                iconName="arrow_up"
                                className={`image-carousel-controls__arrow ${prevArrowActive ? "image-carousel-controls__arrow--active" : ""}`} />
                        </button>
                        <div id='scroller' className='image-carousel__desktop-scroller'>
                            {images.map((image, index) => {
                                return (
                                    <button key={index} className={`image-carousel__desktop-button ${index != 0 ? "image-carousel__margin-top" : ""}`} onClick={() => {
                                        setSelectedImage(image)
                                    }}>
                                        <img
                                            className={`image-carousel__desktop-button-image ${selectedImage === image ? 'image-carousel__desktop-button-image-selected' : ''}`}
                                            src={image.url}
                                        />
                                    </button>
                                )
                            })}
                        </div>
                        <button
                            disabled={!nextArrowActive}
                            onClick={onNextArrowClick}
                            type="button"
                            aria-label="Next"
                        >
                            <SvgIcon
                                iconName="arrow_down"
                                className={`image-carousel-controls__arrow ${nextArrowActive ? "image-carousel-controls__arrow--active" : ""}`} />
                        </button>
                    </div>
                    {selectedImage && <CarouselProductImage image={selectedImage} />}
                </div>
            </TabletLandscapeAndLarger>
            <SmallerThanTabletLandscape>
                <div className='image-carousel image-carousel__column'>
                    <div>
                        {selectedImage && <CarouselProductImage image={selectedImage} />}
                    </div>
                    <div className='image-carousel__mobile'>
                        <div id='scroller' className='image-carousel__mobile-scroller'>
                            {images.map((image, index) => {
                                return (
                                    <button key={index} className={`image-carousel__mobile-button ${index != 0 ? "image-carousel__margin-left" : ""}`} onClick={() => {
                                        setSelectedImage(image)
                                    }}>
                                        <img
                                            className={`image-carousel__mobile-button-image ${selectedImage === image ? 'image-carousel__mobile-button-image-selected' : ''}`}
                                            src={image.url} />
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </SmallerThanTabletLandscape>
        </>
    )
});
